/**
 * Card de una compensacion — Sprint 17 Fase 2.
 *
 * Render visual de una CompensacionConDetalle: header (casa + vecino +
 * badges activa/cerrada + periodicidad), stats (monto recurrente,
 * consumido, restante proyectado en la vigencia), barra de progreso
 * (solo periodicas con vigente_hasta) y grid mes a mes. Para periodicidad
 * 'unica' los stats son simplificados: monto unico + estado.
 *
 * Grid mes a mes (2026-10-06): una compensación APLICADA, sea única o
 * periódica, muestra los meses que pagó su dinero (`cobertura`, calculada
 * por el backend con la misma regla que la grilla de Casas) más el abono
 * que no completa un mes. Antes marcaba ✓ el mes en que se aplicó y la
 * única no tenía grid. Una periódica AÚN NO aplicada muestra su vigencia.
 *
 * No carga datos, no abre modales — solo recibe la compensacion y
 * delega los clicks (onAplicar) al wrapper.
 */
import type { CompensacionConDetalle } from '../../lib/api/types';
import { Button } from '../ui/Button';
import { ESTILO_MES } from '../ui/CeldaMes';
import { formatFecha } from '../../lib/format';
import { hoy } from '../../lib/tiempo';
import { Monto, Num } from './Monto';

interface Props {
  compensacion: CompensacionConDetalle;
  onAplicar: (c: CompensacionConDetalle) => void;
  onEditar: (c: CompensacionConDetalle) => void;
  onCerrar: (c: CompensacionConDetalle) => void;
  onEliminar: (c: CompensacionConDetalle) => void;
}

const MESES = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

/**
 * Extrae año y mes (0-indexado, semántica getMonth) de un ISO
 * `yyyy-mm-dd` por string puro. NUNCA usar `new Date(iso)`: parsea como
 * medianoche UTC y al leerla en hora local (Santiago, UTC-4/-3) retrocede
 * al día anterior, corriendo el mes en los bordes (convención #16).
 */
function ymDeIso(iso: string): { anio: number; mes: number } {
  const [anio, mes] = iso.split('-').map(Number);
  return { anio, mes: mes - 1 };
}

function estaCerrada(c: CompensacionConDetalle): boolean {
  if (!c.activa) return true;
  if (c.vigente_hasta) {
    const fechaHoy = hoy();
    if (c.vigente_hasta < fechaHoy) return true;
  }
  return false;
}

/** N de periodos esperados dentro de la vigencia (solo periodicas). */
function nPeriodos(c: CompensacionConDetalle): number | null {
  if (c.periodicidad === 'unica') return 1;
  if (!c.vigente_hasta) return null;
  const desde = ymDeIso(c.vigente_desde);
  const hasta = ymDeIso(c.vigente_hasta);
  const meses =
    (hasta.anio - desde.anio) * 12 + (hasta.mes - desde.mes) + 1;
  switch (c.periodicidad) {
    case 'mensual':
      return meses;
    case 'bimestral':
      return Math.ceil(meses / 2);
    case 'semestral':
      return Math.ceil(meses / 6);
    case 'anual':
      return Math.ceil(meses / 12);
    default:
      return null;
  }
}

/** Meses dentro de la vigencia del año en curso. Solo para periódicas AÚN
 * NO aplicadas: muestra el compromiso, no plata. */
function mesesVigencia(c: CompensacionConDetalle): {
  mes: number;
  activo: boolean;
}[] {
  const anioActual = Number(hoy().slice(0, 4));
  const desde = ymDeIso(c.vigente_desde);
  const hasta = c.vigente_hasta ? ymDeIso(c.vigente_hasta) : null;

  // Comparación a nivel (año, mes) via índice absoluto anio*12+mes.
  const desdeVal = desde.anio * 12 + desde.mes;
  const hastaVal = hasta ? hasta.anio * 12 + hasta.mes : Infinity;

  return Array.from({ length: 12 }, (_, mes) => {
    const actualVal = anioActual * 12 + mes;
    return { mes, activo: actualVal >= desdeVal && actualVal <= hastaVal };
  });
}

/** Meses (0..11) que pagó la compensación, agrupados por año ascendente.
 * Si no cubrió ningún mes completo, devuelve el año en curso vacío para que
 * la grilla igual muestre que no hay meses pagados. */
function mesesCubiertosPorAnio(
  c: CompensacionConDetalle,
): { anio: number; meses: Set<number> }[] {
  const porAnio = new Map<number, Set<number>>();
  for (const cob of c.cobertura) {
    for (const ym of cob.meses) {
      const { anio, mes } = ymDeIso(`${ym}-01`);
      if (!porAnio.has(anio)) porAnio.set(anio, new Set());
      porAnio.get(anio)!.add(mes);
    }
  }
  if (porAnio.size === 0) {
    porAnio.set(Number(hoy().slice(0, 4)), new Set());
  }
  return [...porAnio.entries()]
    .sort(([a], [b]) => a - b)
    .map(([anio, meses]) => ({ anio, meses }));
}

/** "Ene–Jun 2026", "Mar 2026", "Ene–Feb y Abr 2026": tramos consecutivos. */
function textoMeses(anio: number, meses: Set<number>): string {
  const orden = [...meses].sort((a, b) => a - b);
  const tramos: string[] = [];
  let inicio = orden[0];
  for (let i = 1; i <= orden.length; i++) {
    if (orden[i] !== orden[i - 1] + 1) {
      const fin = orden[i - 1];
      tramos.push(
        inicio === fin ? MESES[inicio] : `${MESES[inicio]}–${MESES[fin]}`,
      );
      inicio = orden[i];
    }
  }
  const lista =
    tramos.length > 1
      ? `${tramos.slice(0, -1).join(', ')} y ${tramos[tramos.length - 1]}`
      : tramos[0];
  return `${lista} ${anio}`;
}

/** "2026-S2" → "S2 2026" (mismo formato que la descripción del gasto);
 * "2025-PRE" → "deuda previa 2025" (deuda anterior a la carga, sin meses). */
function etiquetaPeriodo(periodo: string): string {
  const [anio, sufijo] = periodo.split('-');
  if (sufijo === 'PRE') return `deuda previa ${anio}`;
  return sufijo ? `${sufijo} ${anio}` : periodo;
}

export function CompensacionCard({
  compensacion,
  onAplicar,
  onEditar,
  onCerrar,
  onEliminar,
}: Props) {
  const cerrada = estaCerrada(compensacion);
  const esUnica = compensacion.periodicidad === 'unica';
  const aplicaciones = compensacion.aplicaciones.filter((a) => !a.anulado);
  const tieneHistoria = compensacion.aplicaciones.length > 0;
  const periodos = nPeriodos(compensacion);
  const totalProyectado =
    periodos !== null ? periodos * compensacion.monto : null;
  const restante =
    totalProyectado !== null
      ? Math.max(totalProyectado - compensacion.consumido, 0)
      : null;

  const badgeEstado = cerrada
    ? { text: 'Cerrada', cls: 'bg-text-muted/20 text-text-secondary' }
    : esUnica && aplicaciones.length === 0
      ? { text: 'Pendiente', cls: 'bg-state-warning/20 text-state-warning' }
      : { text: 'Activa', cls: 'bg-state-success/20 text-state-success' };

  const badgePeriodicidad = esUnica
    ? { text: 'Única', cls: 'bg-orange/15 text-orange' }
    : {
        text: compensacion.periodicidad,
        cls: 'bg-bg-sunken text-text-secondary',
      };

  const aplicada = aplicaciones.length > 0;
  const cubiertos = aplicada ? mesesCubiertosPorAnio(compensacion) : null;
  const vigencia = !aplicada && !esUnica ? mesesVigencia(compensacion) : null;
  const abonosParciales = aplicada
    ? compensacion.cobertura.filter((c) => c.abono_parcial > 0)
    : [];

  return (
    <article
      className="bg-bg-card border-[0.5px] border-white/[0.08] rounded-md overflow-hidden flex flex-col"
      data-testid="compensacion-card"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b-[0.5px] border-white/[0.08]">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-text-secondary">
              Casa {compensacion.lote_identificador} ·{' '}
              {compensacion.vecino_nombre}
            </div>
            <h3 className="text-base font-medium text-text-primary mt-0.5 truncate">
              {compensacion.descripcion}
            </h3>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span
              className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${badgeEstado.cls}`}
            >
              {badgeEstado.text}
            </span>
            <span
              className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${badgePeriodicidad.cls}`}
            >
              {badgePeriodicidad.text}
            </span>
          </div>
        </div>
        <div className="text-xs text-text-muted">
          {compensacion.categoria_nombre}
        </div>
      </div>

      {/* Stats */}
      <div className="px-5 py-4 border-b-[0.5px] border-white/[0.08]">
        {esUnica ? (
          <div className="text-center">
            <div className="text-xs uppercase tracking-wide text-text-muted">
              Monto único
            </div>
            <div className="text-2xl font-medium text-text-primary mt-1">
              <Monto value={compensacion.monto} />
            </div>
            {aplicaciones.length > 0 && compensacion.ultima_aplicacion_fecha && (
              <div className="text-xs text-state-success mt-2">
                ✓ Aplicada el {formatFecha(compensacion.ultima_aplicacion_fecha)}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-text-muted">
                Por periodo
              </div>
              <div className="text-sm font-medium text-text-primary mt-0.5">
                <Monto value={compensacion.monto} />
              </div>
              <div className="text-[10px] text-text-muted">
                /{compensacion.periodicidad.slice(0, 3)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-text-muted">
                Consumido
              </div>
              <div className="text-sm font-medium text-text-primary mt-0.5">
                <Monto value={compensacion.consumido} />
              </div>
              <div className="text-[10px] text-text-muted">
                {compensacion.n_aplicaciones} aplic.
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-text-muted">
                Restante
              </div>
              <div className="text-sm font-medium text-text-primary mt-0.5">
                {restante !== null ? <Monto value={restante} /> : '—'}
              </div>
              <div className="text-[10px] text-text-muted">
                {periodos !== null ? (
                  <>de <Monto value={totalProyectado ?? 0} /></>
                ) : (
                  'sin término'
                )}
              </div>
            </div>
          </div>
        )}

        {/* Barra de progreso (solo periodicas con vigente_hasta) */}
        {!esUnica && periodos !== null && totalProyectado !== null && (
          <div className="mt-3">
            <div className="h-1.5 bg-bg-sunken rounded-full overflow-hidden">
              <div
                className="h-full bg-orange rounded-full transition-all"
                style={{
                  width: `${Math.min(
                    (compensacion.consumido / totalProyectado) * 100,
                    100,
                  )}%`,
                }}
                data-testid="progress-bar"
              />
            </div>
            <div className="text-[10px] text-text-muted text-right mt-1">
              {compensacion.n_aplicaciones}/{periodos} ·{' '}
              <Num>{`${Math.round((compensacion.consumido / totalProyectado) * 100)}%`}</Num>
            </div>
          </div>
        )}
      </div>

      {/* Grid mes a mes: lo que pagó (aplicada) o la vigencia (periódica
          sin aplicar). */}
      {cubiertos && (
        <div
          className="px-5 py-3 border-b-[0.5px] border-white/[0.08] space-y-2"
          data-testid="grid-cobertura"
        >
          {cubiertos.map(({ anio, meses }) => (
            <div key={anio}>
              <div className="text-[10px] uppercase tracking-wide text-text-muted mb-2">
                {anio}
              </div>
              <div className="grid grid-cols-12 gap-1">
                {MESES.map((nombre, mes) => {
                  const cubierto = meses.has(mes);
                  return (
                    <div
                      key={mes}
                      className="text-center"
                      title={`${nombre} ${anio}: ${cubierto ? 'compensado' : 'no cubierto'}`}
                    >
                      <div className="text-[9px] text-text-muted">{nombre}</div>
                      <div
                        className={`h-5 mt-0.5 rounded-sm flex items-center justify-center text-[10px] font-semibold ${
                          cubierto ? '' : 'bg-transparent text-text-muted/30'
                        }`}
                        style={
                          cubierto
                            ? {
                                backgroundColor: ESTILO_MES.compensacion.bg,
                                color: ESTILO_MES.compensacion.text,
                              }
                            : undefined
                        }
                      >
                        {cubierto ? ESTILO_MES.compensacion.symbol : '·'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="text-xs text-text-secondary">
            {cubiertos.some(({ meses }) => meses.size > 0) ? (
              <>
                Cubre{' '}
                {cubiertos
                  .filter(({ meses }) => meses.size > 0)
                  .map(({ anio, meses }) => textoMeses(anio, meses))
                  .join(' y ')}
              </>
            ) : (
              'No completa ningún mes'
            )}
            {abonosParciales.map((c) => (
              <span key={c.periodo}>
                {' '}
                · <Monto value={c.abono_parcial} /> a cuenta de{' '}
                {etiquetaPeriodo(c.periodo)}
              </span>
            ))}
          </div>
        </div>
      )}
      {vigencia && (
        <div className="px-5 py-3 border-b-[0.5px] border-white/[0.08]">
          <div className="text-[10px] uppercase tracking-wide text-text-muted mb-2">
            {hoy().slice(0, 4)}
          </div>
          <div className="grid grid-cols-12 gap-1">
            {vigencia.map((m) => (
              <div
                key={m.mes}
                className="text-center"
                title={`${MESES[m.mes]}: ${m.activo ? 'pendiente' : 'fuera de vigencia'}`}
              >
                <div className="text-[9px] text-text-muted">{MESES[m.mes]}</div>
                <div
                  className={`h-5 mt-0.5 rounded-sm flex items-center justify-center text-[10px] ${
                    m.activo
                      ? 'bg-bg-sunken text-text-muted'
                      : 'bg-transparent text-text-muted/30'
                  }`}
                >
                  {m.activo ? '·' : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Motivo de cierre (si aplica) */}
      {cerrada && compensacion.motivo_cierre && (
        <div className="px-5 py-2 border-b-[0.5px] border-white/[0.08] text-xs text-text-muted italic">
          Cerrada: {compensacion.motivo_cierre}
        </div>
      )}

      {/* Footer: vigencia + acciones */}
      <div className="px-5 py-3 flex flex-col gap-2 mt-auto">
        <div className="text-xs text-text-muted">
          {compensacion.vigente_hasta
            ? `${formatFecha(compensacion.vigente_desde)} → ${formatFecha(compensacion.vigente_hasta)}`
            : `Desde ${formatFecha(compensacion.vigente_desde)}`}
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => onEditar(compensacion)}
              className="text-text-secondary hover:text-text-primary underline-offset-2 hover:underline"
            >
              Editar
            </button>
            {!cerrada && (
              <>
                <span className="text-text-muted/50">·</span>
                <button
                  type="button"
                  onClick={() => onCerrar(compensacion)}
                  className="text-text-secondary hover:text-state-warning underline-offset-2 hover:underline"
                >
                  Cerrar
                </button>
              </>
            )}
            {!tieneHistoria && (
              <>
                <span className="text-text-muted/50">·</span>
                <button
                  type="button"
                  onClick={() => onEliminar(compensacion)}
                  className="text-text-secondary hover:text-state-danger underline-offset-2 hover:underline"
                >
                  Eliminar
                </button>
              </>
            )}
          </div>
          {!cerrada && !(esUnica && aplicaciones.length > 0) && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onAplicar(compensacion)}
            >
              Aplicar →
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}
